import React, { useState } from 'react';
import { ChevronDownIcon, SearchIcon, SendIcon, UploadIcon, XIcon } from 'lucide-react';
import { prototypeDocs, type DocStatus } from '../../data/clearDocs';
type Filter = 'all' | 'review' | 'missing' | 'app';
type ListTab = 'draft' | 'final';
type ViewerTab = 'preview' | 'properties';
const FILTERS: {
  id: Filter;
  label: string;
}[] = [{
  id: 'all',
  label: 'All'
}, {
  id: 'review',
  label: 'Needs review'
}, {
  id: 'missing',
  label: 'Missing pages'
}, {
  id: 'app',
  label: 'App'
}];
const STATUS_STYLE: Record<DocStatus, string> = {
  'Needs review': 'bg-[#FCEFA8] text-[#6B5200]',
  Approved: 'bg-[#DCEFD3] text-[#2F6B2A]',
  Pending: 'bg-[#E3ECF7] text-[#2F5585]'
};

/** The working mobile prototype: document list + document viewer, on the product's dark teal board. */
export function ClearDocsPrototype() {
  const [listTab, setListTab] = useState<ListTab>('draft');
  const [filter, setFilter] = useState<Filter>('all');
  const [viewerTab, setViewerTab] = useState<ViewerTab>('preview');
  const docs = prototypeDocs.filter(d => {
    if (listTab === 'final') return d.final;
    if (filter === 'review') return d.status === 'Needs review';
    if (filter === 'missing' || filter === 'app') return false;
    return true;
  });
  const groups = [{
    id: 'review',
    label: 'Needs review',
    items: docs.filter(d => d.group === 'review')
  }, {
    id: 'inc',
    label: 'INC',
    items: docs.filter(d => d.group === 'inc')
  }].filter(g => g.items.length > 0);
  return <div className="rounded-3xl bg-[#1E4B48] px-4 py-8 md:px-10 md:py-12" data-id="element-115" data-mp="37i63nfpz93hl">
      <div className="mx-auto grid max-w-[640px] gap-6 sm:grid-cols-2" data-id="element-116" data-mp="2sukyzhdgew7k">
        {/* Document list */}
        <div className="flex h-[560px] flex-col overflow-hidden rounded-[28px] bg-white p-3 text-[#1B2220] shadow-xl" data-id="element-117" data-mp="2xsuxa0uh6cxe">
          <div className="flex items-center gap-2" data-id="element-118" data-mp="3lhxo9zb01i4k">
            <span className="text-[15px] font-semibold tracking-tight text-[#1E4B48]" data-id="element-119" data-mp="3lh5ril5o1ecp">Clear</span>
            <span className="flex flex-1 items-center gap-1 rounded-full bg-[#F2F4F3] px-2 py-1 text-[9px] text-[#7A837F]" data-id="element-120" data-mp="3qbig19m01fvb">
              <SearchIcon className="h-2.5 w-2.5" aria-hidden data-id="element-121" data-mp="3v62zhu36q7ff" />
              Search leads and loans
            </span>
            <span className="grid h-5 w-5 place-items-center rounded-full bg-[#1E4B48] text-[8px] font-semibold text-white" data-id="element-122" data-mp="3q6glhifj8n5k">
              AB
            </span>
          </div>
          <div className="mt-3 flex items-center justify-between" data-id="element-123" data-mp="3h6juha5o7c7c">
            <p className="text-[11px] font-semibold" data-id="element-124" data-mp="3h8wjg79uimtw">Ryan Smith</p>
            <span className="text-[9px] font-medium text-[#1E4B48]" data-id="element-125" data-mp="3cfbiq7gvber0">Loan details</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5" data-id="element-126" data-mp="37hs1cyoviluu">
            <span className="flex flex-1 items-center justify-between rounded-md border border-[#E3E6E3] px-2 py-1 text-[9px]" data-id="element-127" data-mp="32i71mm1vq9h2">
              URLA <ChevronDownIcon className="h-2.5 w-2.5" aria-hidden data-id="element-128" data-mp="2yf0r327qhw4s" />
            </span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1.5 text-[9px] font-medium" data-id="element-129" data-mp="2tik102gz57p2">
            <span className="rounded-full border border-[#E3E6E3] py-1 text-center text-[#1E4B48]" data-id="element-130" data-mp="18y55r4nffcc0">+ Add requests</span>
            <span className="flex items-center justify-center gap-1 rounded-full bg-[#1E4B48] py-1 text-white" data-id="element-131" data-mp="1dwq6r6ai601i">
              <SendIcon className="h-2.5 w-2.5" aria-hidden data-id="element-132" data-mp="1i0o53es076ti" />
              Send requests · 11
            </span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 text-[9px] text-[#4F5955]" data-id="element-133" data-mp="q8p5mifnllw4">
            <span className="flex items-center gap-1 rounded-full border border-[#E3E6E3] px-2 py-0.5" data-id="element-134" data-mp="q6vuapu3idhs">
              <UploadIcon className="h-2.5 w-2.5" aria-hidden data-id="element-135" data-mp="u7y0jqmfjpt9" /> Upload
            </span>
            <span data-id="element-136" data-mp="z7am881kequ9">Auto reminder: Off</span>
          </div>

          <div role="tablist" aria-label="Document lists" className="mt-3 flex border-b border-[#E3E6E3] text-[10px] font-medium" data-id="element-137" data-mp="141589i4n7hwi">
            {([{
            id: 'draft',
            label: 'Draft documents',
            count: 5
          }, {
            id: 'final',
            label: 'Final in LOS',
            count: 1
          }] as const).map(t => <button key={t.id} type="button" role="tab" aria-selected={listTab === t.id} onClick={() => setListTab(t.id)} className={`-mb-px flex-1 border-b-2 py-1.5 transition-colors duration-150 ${listTab === t.id ? 'border-[#1E4B48] text-[#1E4B48]' : 'border-transparent text-[#7A837F]'}`} data-id="element-138" data-mp="297dafb0q04e6">
                {t.label} <span className="tabular-nums" data-id="element-139" data-mp="2rx339skxag3g">{t.count}</span>
              </button>)}
          </div>

          {listTab === 'draft' && <div className="mt-2 flex gap-1 overflow-hidden" aria-label="Filter by status" data-id="element-140" data-mp="31wbsy98r98k0">
              {FILTERS.map(f => <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)} className={`shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-[9px] font-medium transition-colors duration-150 ${filter === f.id ? 'border-[#1E4B48] bg-[#1E4B48] text-white' : 'border-[#E3E6E3] text-[#4F5955] hover:border-[#1E4B48]/40'}`} data-id="element-141" data-mp="1bko10g3oupia">
                  {f.label}
                </button>)}
            </div>}

          <div className="mt-2 flex-1 overflow-hidden" data-id="element-142" data-mp="dsg6wdl3ylak">
            {groups.length === 0 ? <p className="mt-8 text-center text-[10px] text-[#7A837F]" data-id="element-143" data-mp="imtzy9casgc2">No documents match this filter.</p> : groups.map(g => <div key={g.id} className="mt-1.5" data-id="element-144" data-mp="9pbc6x79rnwh">
                  <p className={`text-[9px] font-semibold uppercase tracking-wide ${g.id === 'review' ? 'text-[#B03F2B]' : 'text-[#4F5955]'}`} data-id="element-145" data-mp="1z3ptiwfyk8hy">
                    {g.label}
                  </p>
                  <ul data-id="element-146" data-mp="21lji8yq782p5">
                    {g.items.map(d => <li key={d.name} className="flex items-start gap-2 border-b border-[#EEF0EE] py-2" data-id="element-147" data-mp="1xkpty48uglvq">
                        <span className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-[3px] border border-[#C9CFCC]" data-id="element-148" data-mp="1sng7mxgr4qc4" />
                        <span className="min-w-0 flex-1" data-id="element-149" data-mp="1nq7nwsfyge8r">
                          <span className="block truncate text-[10px] font-medium" data-id="element-150" data-mp="2latw076dwdkm">{d.name}</span>
                          <span className="block text-[8px] text-[#7A837F]" data-id="element-151" data-mp="2gdtpxw9jbati">{d.uploaded}</span>
                        </span>
                        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[8px] font-semibold ${STATUS_STYLE[d.status]}`} data-id="element-152" data-mp="2bkhfcq1ox4jr">
                          {d.status}
                        </span>
                      </li>)}
                  </ul>
                </div>)}
          </div>
        </div>

        {/* Document viewer */}
        <div className="relative flex h-[560px] flex-col overflow-hidden rounded-[28px] bg-[#F2F4F3] text-[#1B2220] shadow-xl" data-id="element-153" data-mp="36nugvec6m681">
          <div className="flex items-center gap-2 bg-white px-3 pb-2 pt-3" data-id="element-154" data-mp="3bm4ctr5qdaup">
            <span className="min-w-0 flex-1" data-id="element-155" data-mp="3gk5woeazde78">
              <span className="block text-[8px] font-semibold uppercase tracking-wide text-[#1E4B48]" data-id="element-156" data-mp="3lk1z49414xko">Other</span>
              <span className="block truncate text-[10px] font-medium" data-id="element-157" data-mp="3plx35ldchp25">sample-local-pdf.pdf · orphaned pages</span>
            </span>
            <span className="rounded bg-[#F2F4F3] px-1.5 py-0.5 text-[9px] tabular-nums text-[#4F5955]" data-id="element-158" data-mp="3ujfax8fmly8k">1/7</span>
          </div>
          <div role="tablist" aria-label="Viewer" className="flex bg-white text-[10px] font-medium" data-id="element-159" data-mp="385i0suhvvis">
            {(['preview', 'properties'] as const).map(t => <button key={t} type="button" role="tab" aria-selected={viewerTab === t} onClick={() => setViewerTab(t)} className={`flex-1 border-b-2 py-1.5 capitalize transition-colors duration-150 ${viewerTab === t ? 'border-[#1E4B48] text-[#1E4B48]' : 'border-transparent text-[#7A837F]'}`} data-id="element-160" data-mp="d5jmrv722vv9">
                {t}
              </button>)}
          </div>

          {viewerTab === 'preview' ? <div className="flex flex-1 flex-col gap-2 p-3" data-id="element-161" data-mp="jqanjcrjg5at">
              <div className="flex items-center justify-between text-[9px] text-[#4F5955]" data-id="element-162" data-mp="jqu70xwu71qy">
                <span data-id="element-163" data-mp="esslm6bxuqcb">Page 1 / 1</span>
                <span className="font-medium text-[#1E4B48]" data-id="element-164" data-mp="tg3v82ok809s">Categorize</span>
              </div>
              <div className="flex-1 space-y-1.5 rounded-md bg-white p-4" data-id="element-165" data-mp="opds6w3lw9i9">
                <div className="h-1.5 w-1/3 rounded-full bg-[#E3E6E3]" data-id="element-166" data-mp="oo2lanev6lmx" />
                <div className="h-1.5 w-full rounded-full bg-[#EEF0EE]" data-id="element-167" data-mp="yi3i7ug5uf8" />
                <div className="h-1.5 w-11/12 rounded-full bg-[#EEF0EE]" data-id="element-168" data-mp="3s6m8hbior1av" />
                <div className="h-1.5 w-4/5 rounded-full bg-[#EEF0EE]" data-id="element-169" data-mp="27o1wzoooas0b" />
              </div>
            </div> : <div className="flex-1 space-y-3 p-3 text-[10px]" data-id="element-170" data-mp="2cfbhkavd755c">
              {[['Document type', 'Other'], ['Condition', 'Not linked'], ['Uploaded by', 'Borrower']].map(([k, v]) => <div key={k} data-id="element-171" data-mp="22mw2pmfd6woo">
                  <p className="text-[9px] text-[#7A837F]" data-id="element-172" data-mp="2qet2ug9gc09y">{k}</p>
                  <p className="mt-0.5 rounded-md bg-white px-2 py-1.5" data-id="element-173" data-mp="2qcgn89r62202">{v}</p>
                </div>)}
            </div>}

          {/* Categorize sheet */}
          <div className="rounded-t-2xl bg-white p-3 shadow-[0_-8px_24px_rgba(0,0,0,0.08)]" data-id="element-174" data-mp="2hdd3r4o37kl1">
            <div className="flex items-center justify-between" data-id="element-175" data-mp="2mbmwz9q94b40">
              <p className="text-[11px] font-semibold" data-id="element-176" data-mp="15y6lzzfh4a4a">Categorize document</p>
              <XIcon className="h-3 w-3 text-[#7A837F]" aria-hidden data-id="element-177" data-mp="1axsy25jkfkbi" />
            </div>
            <p className="mt-2 text-[9px] text-[#4F5955]" data-id="element-178" data-mp="1avq111jzwqxa">
              Document type <span className="text-[#B03F2B]" data-id="element-179" data-mp="32wcns80zkprs">*</span>
            </p>
            <span className="mt-1 flex items-center justify-between rounded-md border border-[#E3E6E3] px-2 py-1.5 text-[10px]" data-id="element-180" data-mp="36xo7xy13cisl">
              Other <ChevronDownIcon className="h-2.5 w-2.5" aria-hidden data-id="element-181" data-mp="2t5ri60mwakzd" />
            </span>
            <p className="mt-2 text-[9px] text-[#4F5955]" data-id="element-182" data-mp="2y3jklol61pht">Document name</p>
            <span className="mt-1 block truncate rounded-md border border-[#E3E6E3] px-2 py-1.5 text-[10px]" data-id="element-183" data-mp="2j7kcwf1yxrd7">
              sample-local-pdf.pdf · orphaned pages
            </span>
            <span className="mt-3 block rounded-full bg-[#1E4B48] py-1.5 text-center text-[10px] font-semibold text-white" data-id="element-184" data-mp="2a7fa6i7pzh5d">
              Apply
            </span>
          </div>
        </div>
      </div>
    </div>;
}